/**
 * A mark that this browser has begun a trial.
 *
 * Kept out of the auth session on purpose — it has to survive signing out, which is exactly the
 * moment it matters. Reading it is wrapped because storage throws outright in some privacy
 * modes, and a paywall that cannot render is worse than a trial we cannot flag.
 */
const DEVICE_TRIAL_KEY = 'ai_trial_started'

export function hasDeviceTrialMark() {
  try {
    return localStorage.getItem(DEVICE_TRIAL_KEY) === '1'
  } catch {
    return false
  }
}

export function markDeviceTrialStarted() {
  try {
    localStorage.setItem(DEVICE_TRIAL_KEY, '1')
  } catch {
    /* private mode — the server's other signals still apply */
  }
}

/**
 * Stripe Service
 *
 * Centralized frontend client for all Stripe API calls.
 * Components should import from here instead of using api.post('/stripe/...') directly.
 */

import { api } from './api';

export const stripeService = {
  /**
   * Create a Stripe Checkout session for a new subscription.
   * @returns {{ sessionId: string, url: string }}
   */
  /**
   * Start a Premium upgrade for an existing subscriber.
   *
   * Returns a Stripe-hosted confirmation URL — it does NOT charge. The person sees the amount
   * due on Stripe's page and confirms there. Not createSubscriptionSession, which refuses
   * anybody already subscribed and would attach a 3-day trial.
   */
  upgradeToPremium(billingCycle, returnUrl) {
    return api.post('/stripe/upgrade-to-premium', { billingCycle, returnUrl });
  },

  /**
   * Pull the live subscription from Stripe and store it.
   *
   * Used on return from the upgrade confirmation. The webhook that normally records it is
   * asynchronous and has usually not landed by the time somebody is back, so the app would
   * otherwise still believe they are on the old plan and offer them what they just bought.
   */
  syncSubscription() {
    return api.post('/stripe/sync-subscription', {});
  },

  createSubscriptionSession(plan, billingCycle, successUrl, cancelUrl) {
    return api.post('/stripe/create-subscription-session', {
      plan,
      billingCycle,
      successUrl,
      cancelUrl,
      /*
       * Whether THIS BROWSER has started a trial before.
       *
       * The server can see an account's history and other customers on the same email, but not
       * the same person signing up again under a new address — which is the ordinary way a
       * second free trial gets taken. This marker is what notices that, and a repeat attempt is
       * asked for a one dollar card verification rather than refused.
       *
       * Advisory, and trivially cleared: it is one signal the server weighs, never the whole
       * answer. Clearing it costs us a trial, which is the right direction to fail in.
       */
      deviceUsedTrial: hasDeviceTrialMark(),
    });
  },

  /**
   * Verify a completed subscription checkout and apply the plan (webhook
   * fallback — used on the subscription success page).
   * @returns {{ success: boolean, plan?: string, billingCycle?: string }}
   */
  verifySubscription(sessionId) {
    return api.post('/stripe/verify-subscription', { sessionId });
  },

  /**
   * Create a Stripe Checkout session for an additional patent unlock ($5, Pro only).
   * May return { success: true, usedMonthlyLimit: true } if no payment needed.
   */
  createPatentUnlockSession(patentId, successUrl, cancelUrl) {
    return api.post('/stripe/create-patent-unlock-session', {
      patentId,
      successUrl,
      cancelUrl,
    });
  },

  /**
   * Verify payment and unlock a patent (fallback when webhook doesn't fire).
   */
  verifyPatentUnlock(patentId, sessionId) {
    return api.post('/stripe/verify-patent-unlock', { patentId, sessionId });
  },

  /**
   * Get the current user's subscription details.
   */
  getSubscription() {
    return api.get('/stripe/subscription');
  },

  /**
   * Cancel the current subscription at the end of the billing period.
   */
  cancelSubscription() {
    return api.post('/stripe/cancel-subscription');
  },

  /**
   * Reactivate a subscription that was set to cancel at period end.
   */
  reactivateSubscription() {
    return api.post('/stripe/reactivate-subscription');
  },

  /**
   * Preview the prorated cost for a Starter → Pro upgrade.
   */
  getUpgradePreview() {
    return api.get('/stripe/upgrade-preview');
  },

  /**
   * Execute the Starter → Pro upgrade.
   */
  executeUpgrade() {
    return api.post('/stripe/upgrade-starter-to-pro');
  },

  /**
   * Create a Stripe Customer Portal session.
   */
  createPortalSession(returnUrl) {
    return api.post('/stripe/create-portal-session', { returnUrl });
  },
};
